/**
 * useGuestSession — manages localStorage guest token lifecycle for anonymous play.
 *
 * IS-INV-004 carve-out: storing hub-issued guest tokens in localStorage is intentional.
 * IS-INV-004 prohibits caching Clerk session tokens, not hub-issued tokens.
 *
 * @param isLoaded - Clerk's isLoaded flag. Guard prevents fetch during Clerk init phase.
 * @param isSignedIn - Clerk's isSignedIn flag. Only fetch when user is NOT signed in.
 */

import { useState, useEffect } from 'react';
import { apiClient } from '@/services/api-client';
import { logger } from '@/utils/logger';
import { Analytics } from '@/lib/analytics';

const GUEST_TOKEN_KEY = 'hub_guest_token';
const GUEST_USER_ID_KEY = 'hub_guest_user_id';

/**
 * Decode a JWT's payload (base64url) without verifying the signature.
 * Used client-side only to check the `exp` claim.
 */
function getJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const exp = getJwtExp(token);
  if (exp === null) return true;
  return Date.now() / 1000 >= exp;
}

export function useGuestSession(
  isLoaded: boolean,
  isSignedIn: boolean,
  context?: { gameId?: string; challengeId?: string }
): { guestToken: string | null; isReady: boolean; guestError: string | null } {
  const [guestToken, setGuestToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);

  useEffect(() => {
    // Only fetch when Clerk has resolved AND user is not signed in
    if (!isLoaded || isSignedIn) {
      setGuestToken(null);
      setIsReady(isLoaded && isSignedIn); // false while Clerk is still loading
      return;
    }

    const initGuest = async () => {
      // Check localStorage for an existing, non-expired token
      const stored = localStorage.getItem(GUEST_TOKEN_KEY);
      if (stored && !isTokenExpired(stored)) {
        setGuestToken(stored);
        setIsReady(true);
        return;
      }

      // Fetch new guest token (idempotent via X-Guest-User-Id)
      try {
        const existingUserId = localStorage.getItem(GUEST_USER_ID_KEY) ?? undefined;
        const response = await apiClient.getGuestToken(existingUserId);
        localStorage.setItem(GUEST_TOKEN_KEY, response.token);
        localStorage.setItem(GUEST_USER_ID_KEY, response.userId);
        setGuestToken(response.token);
        // T6: Track new anonymous session (not localStorage reuse) — IMA-466
        Analytics.trackAnonymousSessionStart({
          guestUserId: response.userId,
          gameId: context?.gameId,
          challengeId: context?.challengeId
        });
      } catch (error) {
        logger.error('Failed to fetch guest token', {
          error: error instanceof Error ? error.message : 'unknown'
        });
        setGuestError('guest_token_fetch_failed');
      } finally {
        setIsReady(true);
      }
    };

    void initGuest();
  }, [isLoaded, isSignedIn]);

  return { guestToken, isReady, guestError };
}
